import { BaseFeature } from '../../core/base-feature.js';

/**
 * KeystrokeDisplayFeature - Visual keystroke overlay
 * Shows keystrokes with Guitar Hero style animations above windows on the screen preview
 * Includes gaming keyboard overlay with hold indicators, combo display, and mouse tracking
 *
 * Lifecycle:
 * - initialize(): Called during registration (adds CSS)
 * - onActivate(): Called when view mounts (starts animation loop)
 * - onDeactivate(): Called when view unmounts (cleans up DOM elements)
 */
export class KeystrokeDisplayFeature extends BaseFeature {
    constructor(coreAPI) {
        super(coreAPI);

        // Display state
        this.activeKeystrokes = new Map(); // key ID -> keystroke data
        this.heldKeys = new Map(); // keycode -> keystroke data (for keys currently held)
        this.keystrokeIdCounter = 0;
        this.animationFrame = null;
        this.pendingReleases = new Map(); // keycode -> timestamp (keys that got released before held)

        // Gaming keyboard overlay state
        this.pressedGameKeys = new Set(); // keycodes currently pressed
        this.gamingKeyboardElement = null; // DOM element for the keyboard overlay
        this.gamingKeyboardVisible = false;
        this.gamingKeyboardTimeout = null; // auto-hide timer
        this.keyElements = new Map(); // keycode -> DOM element for quick updates

        // Mouse overlay state
        this.mouseElements = new Map(); // button -> DOM element
        this.pressedMouseButtons = new Set();
        this.scrollUpEl = null;
        this.scrollDownEl = null;
        this.scrollFlashTimeout = null;

        // Combo display
        this.comboElement = null;
    }

    static getMetadata() {
        return {
            id: 'keystroke-display',
            name: 'keystroke-display',
            version: 'v1.0',
            capabilities: [
                'display_keystrokes',
                'animate_keystrokes'
            ],
            dependencies: []
        };
    }

    /**
     * Lightweight initialization - NO view-specific DOM access
     * Called during feature registration
     */
    async initialize() {
        console.log('[KeystrokeDisplay] Registered (DOM initialization deferred until activation)');

        // Add CSS for keystroke animations (safe - just adds styles to head)
        this.addStyles();
    }

    /**
     * Activate feature - Initialize DOM elements
     * Called when view mounts
     */
    async onActivate() {
        console.log('[KeystrokeDisplay] Activating - initializing overlay mode...');

        // Start animation loop
        this.startAnimationLoop();

        console.log('[KeystrokeDisplay] Activated');
    }

    /**
     * Deactivate feature - Clean up DOM elements
     * Called when view unmounts
     */
    async onDeactivate() {
        console.log('[KeystrokeDisplay] Deactivating - cleaning up DOM...');

        // Stop animation loop
        this.stopAnimationLoop();

        // Remove all active keystroke bubbles
        this.activeKeystrokes.forEach(({ element }) => {
            if (element && element.parentNode) {
                element.remove();
            }
        });
        this.activeKeystrokes.clear();

        // Clean up all held keys and their timers
        this.heldKeys.forEach((heldData) => {
            if (heldData.cleanupTimeout) {
                clearTimeout(heldData.cleanupTimeout);
            }
            if (heldData.element && heldData.element.parentNode) {
                heldData.element.remove();
            }
        });
        this.heldKeys.clear();

        // Remove all held key bars
        document.querySelectorAll('.keystroke-held-bar').forEach(bar => bar.remove());

        // Clear pending releases
        this.pendingReleases.clear();

        // Clean up gaming keyboard
        this.cleanupGamingKeyboard();

        console.log('[KeystrokeDisplay] Deactivated');
    }

    addStyles() {
        const styleId = 'keystroke-display-styles';
        if (document.getElementById(styleId)) {
            return; // Already added
        }

        // Use the exact foreground window color: #ff6b00 = rgb(255, 107, 0)
        const style = document.createElement('style');
        style.id = styleId;
        style.textContent = `
            /* Main keystroke bubble - matches foreground window color #ff6b00 */
            /* Slightly smaller size for less intrusion */
            /* Centered horizontally on target position via translateX(-50%) */
            .keystroke-bubble {
                position: absolute;
                background: linear-gradient(135deg, rgba(255, 107, 0, 0.95), rgba(220, 90, 0, 0.95));
                color: white;
                padding: 5px 10px;
                border-radius: 6px;
                font-family: 'Courier New', monospace;
                font-size: 14px;
                font-weight: bold;
                pointer-events: none;
                z-index: 1000;
                box-shadow: 0 3px 8px rgba(255, 107, 0, 0.5), 0 0 12px rgba(255, 107, 0, 0.3);
                border: 2px solid rgba(255, 107, 0, 1);
                white-space: pre;
                transform: translateX(-50%);
                will-change: transform, opacity;
            }

            .keystroke-bubble.special-key {
                background: linear-gradient(135deg, rgba(255, 107, 0, 0.95), rgba(200, 80, 0, 0.95));
                border-color: rgba(255, 107, 0, 1);
                box-shadow: 0 3px 8px rgba(255, 107, 0, 0.5), 0 0 12px rgba(255, 107, 0, 0.3);
                color: white;
            }

            .keystroke-bubble.deleted-text {
                text-decoration: line-through;
                text-decoration-color: rgba(255, 50, 50, 0.8);
                text-decoration-thickness: 2px;
                opacity: 0.7;
            }

            /* Fixed held keys bar - positioned at bottom of video */
            .keystroke-held-bar {
                position: absolute;
                bottom: 12px;
                left: 50%;
                transform: translateX(-50%);
                display: flex;
                gap: 10px;
                padding: 8px 16px;
                background: rgba(0, 0, 0, 0.75);
                border-radius: 25px;
                pointer-events: none;
                z-index: 1001;
                align-items: center;
                border: 1px solid rgba(255, 107, 0, 0.3);
            }

            .keystroke-held-bar:empty {
                display: none;
            }

            /* Individual held key badge inside the bar - bigger size */
            .keystroke-held-badge {
                display: flex;
                align-items: center;
                gap: 6px;
                background: linear-gradient(135deg, rgba(255, 107, 0, 0.95), rgba(200, 80, 0, 0.95));
                color: white;
                padding: 6px 14px;
                border-radius: 14px;
                font-family: 'Courier New', monospace;
                font-size: 16px;
                font-weight: bold;
                border: 1px solid rgba(255, 107, 0, 0.8);
                box-shadow: 0 2px 8px rgba(255, 107, 0, 0.5);
                animation: held-badge-pulse 1.5s ease-in-out infinite;
            }

            .keystroke-held-badge .hold-icon {
                font-size: 12px;
                opacity: 0.8;
            }

            .keystroke-held-badge .key-icon {
                font-size: 16px;
            }

            /* Modifier badges get slightly different styling */
            .keystroke-held-badge.modifier {
                background: linear-gradient(135deg, rgba(200, 80, 0, 0.95), rgba(160, 60, 0, 0.95));
                border-color: rgba(200, 80, 0, 0.8);
            }

            @keyframes keystroke-rise {
                0% {
                    transform: translateX(-50%) translateY(0) scale(1);
                    opacity: 1;
                }
                50% {
                    transform: translateX(-50%) translateY(-30px) scale(1.1);
                    opacity: 0.8;
                }
                100% {
                    transform: translateX(-50%) translateY(-60px) scale(0.8);
                    opacity: 0;
                }
            }

            @keyframes keystroke-pulse {
                0%, 100% {
                    box-shadow: 0 4px 12px rgba(255, 107, 0, 0.5), 0 0 20px rgba(255, 107, 0, 0.3);
                }
                50% {
                    box-shadow: 0 4px 16px rgba(255, 107, 0, 0.7), 0 0 30px rgba(255, 107, 0, 0.5);
                }
            }

            @keyframes held-badge-pulse {
                0%, 100% {
                    box-shadow: 0 2px 6px rgba(255, 107, 0, 0.4);
                }
                50% {
                    box-shadow: 0 2px 10px rgba(255, 107, 0, 0.7), 0 0 15px rgba(255, 107, 0, 0.3);
                }
            }

            /* ── Gaming Keyboard Overlay ────────────────────────────── */

            .gaming-keyboard-overlay {
                position: absolute;
                bottom: 50px;
                left: 12px;
                display: flex;
                flex-direction: row;
                gap: 8px;
                padding: 8px;
                background: rgba(0, 0, 0, 0.7);
                border-radius: 8px;
                pointer-events: none;
                z-index: 1002;
                border: 1px solid rgba(255, 107, 0, 0.2);
                opacity: 0;
                transition: opacity 0.3s ease;
                backdrop-filter: blur(4px);
            }

            .gaming-keyboard-overlay.visible {
                opacity: 1;
            }

            .gaming-keyboard-keys {
                display: flex;
                flex-direction: column;
                gap: 3px;
                position: relative;
            }

            .gaming-keyboard-row {
                display: flex;
                gap: 3px;
            }

            /* 1. Row stagger — mimics real keyboard offset */
            .gaming-keyboard-row:nth-child(3) { padding-left: 8px; }  /* Caps/ASDF row */
            .gaming-keyboard-row:nth-child(4) { padding-left: 4px; }  /* Shift/ZXCV row */

            .gaming-key {
                display: flex;
                align-items: center;
                justify-content: center;
                position: relative;
                overflow: hidden;
                min-width: 28px;
                height: 28px;
                padding: 0 4px;
                background: rgba(60, 60, 60, 0.8);
                border: 1px solid rgba(100, 100, 100, 0.5);
                border-radius: 4px;
                color: rgba(200, 200, 200, 0.6);
                font-family: 'Courier New', monospace;
                font-size: 11px;
                font-weight: bold;
                transition: background 0.05s ease, border-color 0.05s ease, color 0.05s ease, box-shadow 0.05s ease;
                box-sizing: border-box;
            }

            .gaming-key.wide-1-5 { min-width: 42px; }
            .gaming-key.wide-2   { min-width: 56px; }
            .gaming-key.space-bar { min-width: 110px; }

            /* 2. WASD highlight zone — subtle accent when idle */
            .gaming-key.wasd {
                border-color: rgba(255, 107, 0, 0.35);
                color: rgba(230, 190, 150, 0.75);
            }

            .gaming-key.pressed {
                background: linear-gradient(135deg, rgba(255, 107, 0, 0.95), rgba(220, 90, 0, 0.95));
                border-color: rgba(255, 140, 40, 0.9);
                color: white;
                box-shadow: 0 0 8px rgba(255, 107, 0, 0.6), inset 0 0 4px rgba(255, 200, 100, 0.3);
                transform: scale(0.95);
            }

            /* 9. Key-up flash — brief white flash on release */
            @keyframes gaming-key-flash {
                0%   { background: rgba(255, 255, 255, 0.7); box-shadow: 0 0 6px rgba(255, 255, 255, 0.4); }
                100% { background: rgba(60, 60, 60, 0.8); box-shadow: none; }
            }
            .gaming-key.flash {
                animation: gaming-key-flash 0.25s ease-out;
            }
            /* WASD keys return to their accent background */
            .gaming-key.wasd.flash {
                animation: gaming-key-flash-wasd 0.25s ease-out;
            }
            @keyframes gaming-key-flash-wasd {
                0%   { background: rgba(255, 255, 255, 0.7); box-shadow: 0 0 6px rgba(255, 255, 255, 0.4); }
                100% { background: rgba(60, 60, 60, 0.8); box-shadow: none; }
            }

            /* 4. Hold duration fill — rises from bottom while key held */
            .gaming-key-fill {
                position: absolute;
                bottom: 0;
                left: 0;
                right: 0;
                height: 0%;
                background: rgba(255, 160, 60, 0.3);
                pointer-events: none;
                animation: gaming-fill-up 3s linear forwards;
            }
            @keyframes gaming-fill-up {
                0%   { height: 0%; }
                100% { height: 100%; }
            }

            /* 6. Combo / chord display */
            .gaming-combo-display {
                position: absolute;
                top: -24px;
                left: 0;
                right: 0;
                text-align: center;
                color: rgba(255, 200, 140, 0.95);
                font-family: 'Courier New', monospace;
                font-size: 12px;
                font-weight: bold;
                pointer-events: none;
                opacity: 0;
                transition: opacity 0.12s ease;
                text-shadow: 0 1px 4px rgba(0, 0, 0, 0.9);
                white-space: nowrap;
            }
            .gaming-combo-display.visible {
                opacity: 1;
            }

            /* 7. Mouse overlay section */
            .gaming-mouse-section {
                display: flex;
                flex-direction: column;
                gap: 3px;
                align-self: flex-end;
                padding-bottom: 2px;
            }

            .gaming-mouse-top {
                display: flex;
                gap: 2px;
            }

            .gaming-mouse-btn {
                display: flex;
                align-items: center;
                justify-content: center;
                position: relative;
                overflow: hidden;
                min-width: 24px;
                height: 32px;
                padding: 0 2px;
                background: rgba(50, 50, 55, 0.8);
                border: 1px solid rgba(90, 90, 95, 0.5);
                border-radius: 4px 4px 2px 2px;
                color: rgba(180, 180, 180, 0.5);
                font-family: 'Courier New', monospace;
                font-size: 9px;
                font-weight: bold;
                transition: background 0.05s ease, border-color 0.05s ease, color 0.05s ease;
                box-sizing: border-box;
            }

            .gaming-mouse-btn.pressed {
                background: linear-gradient(135deg, rgba(255, 107, 0, 0.9), rgba(220, 90, 0, 0.9));
                border-color: rgba(255, 140, 40, 0.8);
                color: white;
                box-shadow: 0 0 6px rgba(255, 107, 0, 0.5);
            }

            @keyframes gaming-mouse-flash {
                0%   { background: rgba(255, 255, 255, 0.6); }
                100% { background: rgba(50, 50, 55, 0.8); }
            }
            .gaming-mouse-btn.flash {
                animation: gaming-mouse-flash 0.2s ease-out;
            }

            .gaming-mouse-mmb {
                min-width: 50px;
                height: 18px;
                border-radius: 2px 2px 6px 6px;
                font-size: 8px;
            }

            .gaming-mouse-scroll {
                display: flex;
                justify-content: center;
                gap: 6px;
                height: 14px;
                align-items: center;
            }

            .gaming-scroll-indicator {
                font-size: 10px;
                color: rgba(120, 120, 120, 0.4);
                transition: color 0.05s ease, text-shadow 0.05s ease;
            }
            .gaming-scroll-indicator.active {
                color: rgba(255, 160, 60, 0.95);
                text-shadow: 0 0 4px rgba(255, 107, 0, 0.6);
            }
        `;
        document.head.appendChild(style);
    }

    async handleMessage(message) {
        const { action, payload } = message;
        switch (action) {
            case 'key_down':
                return this.handleGameKeyDown(payload);
            case 'key_up':
                return this.handleGameKeyUp(payload);
            case 'mouse_down':
                return this.handleMouseDown(payload);
            case 'mouse_up':
                return this.handleMouseUp(payload);
            case 'mouse_wheel':
                return this.handleMouseWheel(payload);
            case 'key_held':
                return this.handleKeyHeld(payload);
            case 'key_released':
                return this.handleKeyReleased(payload);
            case 'keystroke_word':
                return this.handleKeystrokeWord(payload);
            case 'keystroke':
                return;
            default:
                console.log(`[KeystrokeDisplay] Unknown action: ${action}`);
        }
    }

    /**
     * Handle key held event (for visual feedback overlay)
     */
    handleKeyHeld(data) {
        const { key, code, keycode, isModifier, window: windowInfo, timestamp } = data;

        // Check if we received key_released before key_held (out of order)
        if (this.pendingReleases.has(keycode)) {
            this.pendingReleases.delete(keycode);
            return;
        }

        // Check if this key is already held - if so, clean it up first
        const existing = this.heldKeys.get(keycode);
        if (existing) {
            this.cleanupHeldKey(keycode);
        }

        // Find all screen share video elements
        const screenShareVideos = document.querySelectorAll('[data-type="Screen-Share"] video');

        screenShareVideos.forEach(video => {
            const videoWrapper = video.parentElement;
            if (!videoWrapper) return;

            // Get video dimensions
            const displayWidth = video.clientWidth;
            const displayHeight = video.clientHeight;

            if (!displayWidth || !displayHeight) return;

            // Use native resolution from window info, or video dimensions as fallback
            const sourceWidth = (windowInfo && windowInfo.screenWidth) || video.videoWidth || displayWidth;
            const sourceHeight = (windowInfo && windowInfo.screenHeight) || video.videoHeight || displayHeight;

            // Simple direct scale calculation (container aspect ratio matches video, no letterboxing)
            const scaleX = displayWidth / sourceWidth;
            const scaleY = displayHeight / sourceHeight;

            // Determine position based on window info
            let displayX, displayY;

            if (windowInfo && windowInfo.x !== undefined && windowInfo.y !== undefined) {
                // Calculate screen coordinates (center of window, 30px below top)
                const screenX = windowInfo.x + windowInfo.width / 2;
                const screenY = windowInfo.y + 30;

                // Convert to display coordinates
                displayX = screenX * scaleX;
                displayY = screenY * scaleY;
            } else {
                // Default to center of screen
                displayX = displayWidth / 2;
                displayY = displayHeight / 2;
            }

            // Add some horizontal randomness to avoid stacking
            displayX += (Math.random() - 0.5) * 40;

            // Create held keystroke bubble with modifier flag
            this.createHeldKeystrokeBubble(videoWrapper, key, code, displayX, displayY, keycode, isModifier);
        });
    }

    /**
     * Handle key released event (remove visual feedback)
     */
    handleKeyReleased(data) {
        const { keycode, timestamp } = data;

        const heldData = this.heldKeys.get(keycode);
        if (heldData) {
            // Normal case: we have the held key, clean it up
            this.cleanupHeldKey(keycode);
        } else {
            // Out-of-order case: key_released arrived before key_held
            this.pendingReleases.set(keycode, Date.now());

            // Auto-clear pending release after 1 second
            setTimeout(() => {
                if (this.pendingReleases.has(keycode)) {
                    this.pendingReleases.delete(keycode);
                }
            }, 1000);
        }
    }

    /**
     * Clean up a held key (remove badge and clear timeout)
     */
    cleanupHeldKey(keycode) {
        const heldData = this.heldKeys.get(keycode);
        if (!heldData) return;

        // Clear auto-cleanup timeout if exists
        if (heldData.cleanupTimeout) {
            clearTimeout(heldData.cleanupTimeout);
        }

        // Fade out and remove the badge
        if (heldData.element && heldData.element.parentNode) {
            heldData.element.style.transition = 'opacity 0.2s ease-out, transform 0.2s ease-out';
            heldData.element.style.opacity = '0';
            heldData.element.style.transform = 'scale(0.8)';
            setTimeout(() => {
                if (heldData.element && heldData.element.parentNode) {
                    heldData.element.remove();
                }
            }, 200);
        }

        this.heldKeys.delete(keycode);
    }

    /**
     * Handle word batch - display whole word as a bubble
     * 5. Suppressed when gaming keyboard is visible (avoids "wwwssaadd" noise)
     */
    handleKeystrokeWord(data) {
        // Gaming mode: suppress guitar-hero word bubbles
        if (this.gamingKeyboardVisible) return;

        const { word, keystrokes, window: windowInfo, timestamp } = data;

        // Display the whole word as a single bubble on the video overlay
        const screenShareVideos = document.querySelectorAll('[data-type="Screen-Share"] video');

        screenShareVideos.forEach(video => {
            const videoWrapper = video.parentElement;
            if (!videoWrapper) return;

            const displayWidth = video.clientWidth;
            const displayHeight = video.clientHeight;

            if (!displayWidth || !displayHeight) return;

            // Use native resolution from window info, or video dimensions as fallback
            const sourceWidth = (windowInfo && windowInfo.screenWidth) || video.videoWidth || displayWidth;
            const sourceHeight = (windowInfo && windowInfo.screenHeight) || video.videoHeight || displayHeight;

            // Simple direct scale calculation (container aspect ratio matches video, no letterboxing)
            const scaleX = displayWidth / sourceWidth;
            const scaleY = displayHeight / sourceHeight;

            // Position at bottom center of the active window
            let displayX, displayY;

            if (windowInfo && windowInfo.x !== undefined && windowInfo.width !== undefined &&
                windowInfo.y !== undefined && windowInfo.height !== undefined) {
                // Calculate screen coordinates (center of window, 70px above bottom)
                const screenX = windowInfo.x + windowInfo.width / 2;
                const screenY = windowInfo.y + windowInfo.height - 70;

                // Convert to display coordinates
                displayX = screenX * scaleX;
                displayY = screenY * scaleY;

                // Clamp to stay within video bounds
                const minY = 50;
                const maxY = displayHeight - 50;
                displayY = Math.min(displayY, maxY);
                displayY = Math.max(displayY, minY);
            } else {
                // Default to center bottom of video if no window info
                displayX = displayWidth / 2;
                displayY = displayHeight - 70;
            }

            // Add small horizontal randomness to avoid exact stacking
            displayX += (Math.random() - 0.5) * 30;

            // Create word bubble with keystroke history for backspace handling
            this.createWordBubble(videoWrapper, word, displayX, displayY, keystrokes);
        });
    }

    /**
     * Process keystrokes to create display text with backspace handling
     * Returns an array of {text, deleted} segments
     */
    processKeystrokesForDisplay(keystrokes) {
        if (!keystrokes || keystrokes.length === 0) {
            return [];
        }

        const segments = [];
        let currentText = '';
        let deletedText = '';
        let backspaceCount = 0;

        for (let i = 0; i < keystrokes.length; i++) {
            const ks = keystrokes[i];
            const key = ks.key || ks;

            if (key === 'Backspace') {
                backspaceCount++;
                // If we have current text, mark the last character as deleted
                if (currentText.length > 0) {
                    deletedText = currentText.slice(-1) + deletedText;
                    currentText = currentText.slice(0, -1);
                }
            } else {
                // If we accumulated deleted text, add it as a segment
                if (deletedText.length > 0 || backspaceCount > 0) {
                    if (currentText.length > 0) {
                        segments.push({ text: currentText, deleted: false });
                        currentText = '';
                    }
                    if (deletedText.length > 0) {
                        segments.push({ text: deletedText, deleted: true });
                        deletedText = '';
                    } else if (backspaceCount > 0) {
                        // Backspaces with nothing to delete - show icon with count
                        segments.push({ text: `⌫${backspaceCount > 1 ? '×' + backspaceCount : ''}`, deleted: false, isIcon: true });
                    }
                    backspaceCount = 0;
                }
                // Add the character to current text
                if (key.length === 1) {
                    currentText += key;
                } else if (key === ' ') {
                    currentText += ' ';
                }
            }
        }

        // Finalize any remaining segments
        if (deletedText.length > 0 || backspaceCount > 0) {
            if (currentText.length > 0) {
                segments.push({ text: currentText, deleted: false });
                currentText = '';
            }
            if (deletedText.length > 0) {
                segments.push({ text: deletedText, deleted: true });
            } else if (backspaceCount > 0) {
                segments.push({ text: `⌫${backspaceCount > 1 ? '×' + backspaceCount : ''}`, deleted: false, isIcon: true });
            }
        } else if (currentText.length > 0) {
            segments.push({ text: currentText, deleted: false });
        }

        return segments;
    }

    /**
     * Create a word bubble (displays whole word, auto-fades)
     * Handles backspace by showing deleted characters with strikethrough
     * Positioned at bottom of screen, rises upward and fades
     */
    createWordBubble(container, word, x, y, keystrokes) {
        const bubble = document.createElement('div');
        bubble.className = 'keystroke-bubble';
        bubble.style.left = `${x}px`;
        bubble.style.top = `${y}px`;
        // Uses smaller default font-size from CSS (14px)

        // Process keystrokes to handle backspaces
        const segments = this.processKeystrokesForDisplay(keystrokes);

        if (segments.length > 0) {
            // Build the bubble content with styled segments
            segments.forEach(segment => {
                const span = document.createElement('span');
                span.textContent = segment.text;
                if (segment.deleted) {
                    span.style.textDecoration = 'line-through';
                    span.style.textDecorationColor = 'rgba(255, 50, 50, 0.9)';
                    span.style.textDecorationThickness = '2px';
                    span.style.opacity = '0.6';
                }
                if (segment.isIcon) {
                    span.style.opacity = '0.8';
                }
                bubble.appendChild(span);
            });
        } else {
            // Fallback to simple text if no keystrokes provided
            bubble.textContent = word;
        }

        // Ensure container is positioned
        if (container.style.position !== 'absolute' && container.style.position !== 'relative') {
            container.style.position = 'relative';
        }

        container.appendChild(bubble);

        // Apply animation - rises upward from bottom position
        bubble.style.animation = 'keystroke-rise 2s ease-out forwards, keystroke-pulse 0.5s ease-out';

        // Remove after animation
        setTimeout(() => {
            bubble.remove();
        }, 2000);
    }

    /**
     * Get or create the held keys bar for a video container
     */
    getOrCreateHeldBar(container) {
        let bar = container.querySelector('.keystroke-held-bar');
        if (!bar) {
            bar = document.createElement('div');
            bar.className = 'keystroke-held-bar';

            // Ensure container is positioned
            if (container.style.position !== 'absolute' && container.style.position !== 'relative') {
                container.style.position = 'relative';
            }

            container.appendChild(bar);
        }
        return bar;
    }

    /**
     * Create a held keystroke badge in the fixed bar at the bottom
     * Keeps held keys separate from floating word bubbles
     */
    createHeldKeystrokeBubble(container, key, code, x, y, keycode, isModifier) {
        // Remove previous badge for this keycode if it exists
        const existing = this.heldKeys.get(keycode);
        if (existing) {
            if (existing.element && existing.element.parentNode) {
                existing.element.remove();
            }
            if (existing.cleanupTimeout) {
                clearTimeout(existing.cleanupTimeout);
            }
        }

        // Get or create the held keys bar
        const bar = this.getOrCreateHeldBar(container);

        // Create held key badge
        const badge = document.createElement('div');
        badge.className = 'keystroke-held-badge';
        badge.setAttribute('data-keycode', keycode);

        // Add modifier class for slightly different styling
        if (isModifier) {
            badge.classList.add('modifier');
        }

        // Get the icon for this key
        const keyIcon = this.getHeldKeyIcon(key, code, isModifier);

        // Create the badge content with hold icon
        const holdIcon = document.createElement('span');
        holdIcon.className = 'hold-icon';
        holdIcon.textContent = '⬇';

        const keySpan = document.createElement('span');
        keySpan.className = 'key-icon';
        keySpan.textContent = keyIcon;

        badge.appendChild(holdIcon);
        badge.appendChild(keySpan);

        bar.appendChild(badge);

        // Auto-cleanup after 10 seconds if key_released is never received
        const cleanupTimeout = setTimeout(() => {
            console.log(`[KeystrokeDisplay] Auto-cleaning up orphaned held key ${key} (keycode ${keycode}) after 10s`);
            this.cleanupHeldKey(keycode);
        }, 10000);

        // Store held key data
        this.heldKeys.set(keycode, {
            element: badge,
            key: key,
            keycode: keycode,
            startTime: Date.now(),
            cleanupTimeout: cleanupTimeout,
            isModifier: isModifier
        });
    }

    formatKeyForDisplay(key, code) {
        // Comprehensive special key → Unicode symbol map
        const keyMap = {
            ' ': '␣',
            'Enter': '↵', 'Backspace': '⌫', 'Delete': '⌦', 'Tab': '⇥',
            'Escape': 'Esc', 'Space': '␣',
            'ArrowUp': '↑', 'ArrowDown': '↓', 'ArrowLeft': '←', 'ArrowRight': '→',
            'Home': '⇤', 'End': '⇥', 'PageUp': '⇞', 'PageDown': '⇟',
            'Shift': '⇧', 'Control': 'Ctrl', 'Alt': 'Alt', 'Meta': '⌘',
            'CapsLock': '⇪', 'Insert': 'Ins', 'PrintScreen': 'PrtSc',
            'ScrollLock': 'ScrLk', 'Pause': 'Pause', 'NumLock': 'NumLk',
            'ContextMenu': '☰',
            'F1': 'F1', 'F2': 'F2', 'F3': 'F3', 'F4': 'F4',
            'F5': 'F5', 'F6': 'F6', 'F7': 'F7', 'F8': 'F8',
            'F9': 'F9', 'F10': 'F10', 'F11': 'F11', 'F12': 'F12',
        };

        if (keyMap[key]) return keyMap[key];
        if (key.length === 1) return key;
        return key;
    }

    /**
     * Get icon for held key indicator
     */
    getHeldKeyIcon(key, code, isModifier) {
        const symbol = this.formatKeyForDisplay(key, code);
        // For regular single chars, return uppercase
        if (symbol === key && key.length === 1) return key.toUpperCase();
        return symbol;
    }

    // ── Gaming Keyboard Overlay ──────────────────────────────────────

    // Left-half keyboard layout: [keycode, label, cssClass?]
    // Keycodes from uiohook scan codes (Windows Set 1)
    static GAMING_KEYBOARD_LAYOUT = [
        // Row 0: number row
        [
            [41, '`'], [2, '1'], [3, '2'], [4, '3'], [5, '4'], [6, '5'],
        ],
        // Row 1: QWERTY row
        [
            [15, 'Tab', 'wide-1-5'], [16, 'Q'], [17, 'W'], [18, 'E'], [19, 'R'], [20, 'T'],
        ],
        // Row 2: Home row
        [
            [58, 'Cap', 'wide-1-5'], [30, 'A'], [31, 'S'], [32, 'D'], [33, 'F'], [34, 'G'],
        ],
        // Row 3: Bottom row
        [
            [42, 'Shift', 'wide-2'], [44, 'Z'], [45, 'X'], [46, 'C'], [47, 'V'], [48, 'B'],
        ],
        // Row 4: Modifier row
        [
            [29, 'Ctrl', 'wide-1-5'], [56, 'Alt', 'wide-1-5'], [57, '⎵', 'space-bar'],
        ],
    ];

    // WASD keycodes for highlight
    static WASD_KEYCODES = new Set([17, 30, 31, 32]); // W, A, S, D

    // Set of keycodes that belong to the gaming keyboard
    static GAMING_KEYCODES = new Set(
        KeystrokeDisplayFeature.GAMING_KEYBOARD_LAYOUT.flat().map(k => k[0])
    );

    // Keycode → combo label (for combo display)
    static KEYCODE_COMBO_LABELS = new Map(
        KeystrokeDisplayFeature.GAMING_KEYBOARD_LAYOUT.flat().map(([kc, label]) => {
            // Use short symbols for modifiers in combo display
            if (kc === 42) return [kc, '⇧'];
            if (kc === 29) return [kc, 'Ctrl'];
            if (kc === 56) return [kc, 'Alt'];
            if (kc === 57) return [kc, '␣'];
            if (kc === 15) return [kc, 'Tab'];
            if (kc === 58) return [kc, 'Caps'];
            return [kc, label.toUpperCase()];
        })
    );

    // Modifier sort priority for combo display (lower = shown first)
    static COMBO_PRIORITY = new Map([[29, 0], [42, 1], [56, 2]]);

    handleGameKeyDown(data) {
        const { keycode } = data;
        if (!KeystrokeDisplayFeature.GAMING_KEYCODES.has(keycode)) return;

        this.pressedGameKeys.add(keycode);

        const el = this.keyElements.get(keycode);
        if (el) {
            // Remove flash class if still playing from previous release
            el.classList.remove('flash');

            // Set pressed state
            el.classList.add('pressed');

            // 4. Add hold duration fill element
            if (!el.querySelector('.gaming-key-fill')) {
                const fill = document.createElement('div');
                fill.className = 'gaming-key-fill';
                el.appendChild(fill);
            }
        }

        this.showGamingKeyboard();
        this.updateComboDisplay();
    }

    handleGameKeyUp(data) {
        const { keycode } = data;
        if (!KeystrokeDisplayFeature.GAMING_KEYCODES.has(keycode)) return;

        this.pressedGameKeys.delete(keycode);

        const el = this.keyElements.get(keycode);
        if (el) {
            el.classList.remove('pressed');

            // 4. Remove hold fill
            const fill = el.querySelector('.gaming-key-fill');
            if (fill) fill.remove();

            // 9. Key-up flash
            el.classList.remove('flash');
            // Force reflow so re-adding triggers new animation
            void el.offsetWidth;
            el.classList.add('flash');
            el.addEventListener('animationend', () => {
                el.classList.remove('flash');
            }, { once: true });
        }

        this.updateComboDisplay();
        this.resetGamingKeyboardTimeout();
    }

    // ── Mouse Overlay ────────────────────────────────────────────────

    // uiohook button: 1=LMB, 2=RMB, 3=MMB
    handleMouseDown(data) {
        const { button } = data;
        if (button < 1 || button > 3) return;

        this.pressedMouseButtons.add(button);

        const el = this.mouseElements.get(button);
        if (el) {
            el.classList.remove('flash');
            el.classList.add('pressed');
        }

        // Mouse clicks also show the gaming keyboard if it exists
        if (this.gamingKeyboardElement) {
            this.showGamingKeyboard();
        }
    }

    handleMouseUp(data) {
        const { button } = data;
        if (button < 1 || button > 3) return;

        this.pressedMouseButtons.delete(button);

        const el = this.mouseElements.get(button);
        if (el) {
            el.classList.remove('pressed');
            el.classList.remove('flash');
            void el.offsetWidth;
            el.classList.add('flash');
            el.addEventListener('animationend', () => {
                el.classList.remove('flash');
            }, { once: true });
        }

        this.resetGamingKeyboardTimeout();
    }

    handleMouseWheel(data) {
        const { rotation } = data;
        // rotation > 0 = scroll down, < 0 = scroll up
        const indicator = rotation < 0 ? this.scrollUpEl : this.scrollDownEl;
        if (!indicator) return;

        indicator.classList.add('active');

        // Clear previous timeout for this direction
        const timeoutKey = rotation < 0 ? '_scrollUpTimeout' : '_scrollDownTimeout';
        if (this[timeoutKey]) clearTimeout(this[timeoutKey]);
        this[timeoutKey] = setTimeout(() => {
            indicator.classList.remove('active');
        }, 150);

        // Also keep keyboard visible during scrolling
        if (this.gamingKeyboardElement) {
            this.showGamingKeyboard();
        }
    }

    // ── Combo Display ────────────────────────────────────────────────

    updateComboDisplay() {
        if (!this.comboElement) return;

        if (this.pressedGameKeys.size < 2) {
            this.comboElement.classList.remove('visible');
            return;
        }

        // Build sorted combo label: modifiers first, then alphabetical
        const keys = [...this.pressedGameKeys];
        keys.sort((a, b) => {
            const pa = KeystrokeDisplayFeature.COMBO_PRIORITY.get(a) ?? 3;
            const pb = KeystrokeDisplayFeature.COMBO_PRIORITY.get(b) ?? 3;
            if (pa !== pb) return pa - pb;
            const la = KeystrokeDisplayFeature.KEYCODE_COMBO_LABELS.get(a) || '?';
            const lb = KeystrokeDisplayFeature.KEYCODE_COMBO_LABELS.get(b) || '?';
            return la.localeCompare(lb);
        });

        const label = keys
            .map(kc => KeystrokeDisplayFeature.KEYCODE_COMBO_LABELS.get(kc) || '?')
            .join('+');

        this.comboElement.textContent = label;
        this.comboElement.classList.add('visible');
    }

    // ── Keyboard Lifecycle ───────────────────────────────────────────

    showGamingKeyboard() {
        // Reset auto-hide timer
        this.resetGamingKeyboardTimeout();

        if (this.gamingKeyboardVisible) return;

        // Create the overlay if it doesn't exist yet
        if (!this.gamingKeyboardElement) {
            this.createGamingKeyboard();
        }

        // Attach to screen share video wrapper
        const video = document.querySelector('[data-type="Screen-Share"] video');
        if (!video) return;
        const wrapper = video.parentElement;
        if (!wrapper) return;

        if (wrapper.style.position !== 'absolute' && wrapper.style.position !== 'relative') {
            wrapper.style.position = 'relative';
        }

        if (!this.gamingKeyboardElement.parentNode) {
            wrapper.appendChild(this.gamingKeyboardElement);
        }

        // Force reflow then show
        this.gamingKeyboardElement.offsetHeight;
        this.gamingKeyboardElement.classList.add('visible');
        this.gamingKeyboardVisible = true;
    }

    hideGamingKeyboard() {
        if (!this.gamingKeyboardVisible || !this.gamingKeyboardElement) return;

        // Don't hide if keys or mouse buttons are still pressed
        if (this.pressedGameKeys.size > 0 || this.pressedMouseButtons.size > 0) {
            this.resetGamingKeyboardTimeout();
            return;
        }

        this.gamingKeyboardElement.classList.remove('visible');
        this.gamingKeyboardVisible = false;

        // Hide combo display
        if (this.comboElement) {
            this.comboElement.classList.remove('visible');
        }
    }

    resetGamingKeyboardTimeout() {
        if (this.gamingKeyboardTimeout) clearTimeout(this.gamingKeyboardTimeout);
        this.gamingKeyboardTimeout = setTimeout(() => {
            this.hideGamingKeyboard();
        }, 3000); // Hide 3s after last key activity
    }

    createGamingKeyboard() {
        const overlay = document.createElement('div');
        overlay.className = 'gaming-keyboard-overlay';

        // ── Keyboard section (left) ──
        const keysSection = document.createElement('div');
        keysSection.className = 'gaming-keyboard-keys';

        // 6. Combo display (positioned above keyboard)
        this.comboElement = document.createElement('div');
        this.comboElement.className = 'gaming-combo-display';
        keysSection.appendChild(this.comboElement);

        for (const row of KeystrokeDisplayFeature.GAMING_KEYBOARD_LAYOUT) {
            const rowEl = document.createElement('div');
            rowEl.className = 'gaming-keyboard-row';

            for (const [keycode, label, cssClass] of row) {
                const keyEl = document.createElement('div');
                keyEl.className = 'gaming-key';
                if (cssClass) keyEl.classList.add(cssClass);
                keyEl.textContent = label;
                keyEl.setAttribute('data-keycode', keycode);

                // 2. WASD highlight
                if (KeystrokeDisplayFeature.WASD_KEYCODES.has(keycode)) {
                    keyEl.classList.add('wasd');
                }

                this.keyElements.set(keycode, keyEl);
                rowEl.appendChild(keyEl);
            }

            keysSection.appendChild(rowEl);
        }

        overlay.appendChild(keysSection);

        // ── Mouse section (right) ──
        const mouseSection = document.createElement('div');
        mouseSection.className = 'gaming-mouse-section';

        // Top row: LMB + RMB
        const mouseTop = document.createElement('div');
        mouseTop.className = 'gaming-mouse-top';

        const lmb = document.createElement('div');
        lmb.className = 'gaming-mouse-btn';
        lmb.textContent = 'L';
        this.mouseElements.set(1, lmb);

        const rmb = document.createElement('div');
        rmb.className = 'gaming-mouse-btn';
        rmb.textContent = 'R';
        this.mouseElements.set(2, rmb);

        mouseTop.appendChild(lmb);
        mouseTop.appendChild(rmb);
        mouseSection.appendChild(mouseTop);

        // Scroll indicators
        const scrollRow = document.createElement('div');
        scrollRow.className = 'gaming-mouse-scroll';

        this.scrollUpEl = document.createElement('span');
        this.scrollUpEl.className = 'gaming-scroll-indicator';
        this.scrollUpEl.textContent = '▲';

        this.scrollDownEl = document.createElement('span');
        this.scrollDownEl.className = 'gaming-scroll-indicator';
        this.scrollDownEl.textContent = '▼';

        scrollRow.appendChild(this.scrollUpEl);
        scrollRow.appendChild(this.scrollDownEl);
        mouseSection.appendChild(scrollRow);

        // MMB
        const mmb = document.createElement('div');
        mmb.className = 'gaming-mouse-btn gaming-mouse-mmb';
        mmb.textContent = 'M';
        this.mouseElements.set(3, mmb);
        mouseSection.appendChild(mmb);

        overlay.appendChild(mouseSection);

        this.gamingKeyboardElement = overlay;
    }

    cleanupGamingKeyboard() {
        if (this.gamingKeyboardTimeout) {
            clearTimeout(this.gamingKeyboardTimeout);
            this.gamingKeyboardTimeout = null;
        }
        if (this._scrollUpTimeout) { clearTimeout(this._scrollUpTimeout); this._scrollUpTimeout = null; }
        if (this._scrollDownTimeout) { clearTimeout(this._scrollDownTimeout); this._scrollDownTimeout = null; }
        if (this.gamingKeyboardElement && this.gamingKeyboardElement.parentNode) {
            this.gamingKeyboardElement.remove();
        }
        this.gamingKeyboardElement = null;
        this.gamingKeyboardVisible = false;
        this.pressedGameKeys.clear();
        this.pressedMouseButtons.clear();
        this.keyElements.clear();
        this.mouseElements.clear();
        this.comboElement = null;
        this.scrollUpEl = null;
        this.scrollDownEl = null;
    }

    startAnimationLoop() {
        const animate = () => {
            // Animation loop for any additional effects
            this.animationFrame = requestAnimationFrame(animate);
        };
        animate();
    }

    stopAnimationLoop() {
        if (this.animationFrame) {
            cancelAnimationFrame(this.animationFrame);
            this.animationFrame = null;
        }
    }

    async cleanup() {
        console.log('[KeystrokeDisplay] Cleaning up...');
        this.cleanupGamingKeyboard();
        await this.onDeactivate();
    }
}
